import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux/es/exports";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import AddButton from "../../components/AddButton";
import Pagination from "../../components/Pagination";
import Select from "../../components/Select";
import {
    deleteWorkshopServiceById,
    fetchWorkshopServiceTable,
} from "../../services/workshopServiceService";
import EditButton from "../../components/EditButton";
import DeleteButton from "../../components/DeleteButton";
import Loading from "../../components/loading";
import { toast } from "react-toastify";
import TempDataTable from "../../components/TemDataTable";

const headerTable = [
    { name: "name", sort: true },
    { name: "service_type", sort: false },
    { name: "workshop_service_calculation_type", sort: false },
    { name: "price", sort: true },
    { name: "currency", sort: false },
    <span className="sr-only">Edit</span>,
];

const bodyTable = ["name", "serviceType", "calculationType", "price", "currency", "actions"];

function WorkshopServices({ t }) {
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [itemOffset, setItemOffset] = useState(0);
    const [forcePage, setForcePage] = useState(0);
    const [data, setData] = useState({
        data: [],
        recordsTotal: 0,
        pageCount: 1,
    });
    const [isLoading, setIsLoading] = useState(true);
    const { token } = useSelector((state) => state.user);
    const [sortColumn, setSortColumn] = useState("id");
    const [sortDir, setSortDir] = useState("DESC");
    const [clear, setClear] = useState(false);
    const navigate = useNavigate();

    const onDeleteItem = async (id) => {
        const responseReceived = await swal({
            title: t(`action.delete_item.title`),
            text: t(`action.delete_item.text`),
            icon: "warning",
            buttons: {
                cancel: true,
                confirm: true,
            },
        });
        if (responseReceived) {
            setIsLoading(true);
            const response = await deleteWorkshopServiceById(
                `${process.env.REACT_APP_HOST_BASE_URL}/v1/workshop-service/${id}`,
                token
            );
            if (response.success) {
                fetchData();
                toast.success(t("action.delete_success"), {
                    position: "bottom-right",
                    autoClose: 3000,
                    closeOnClick: true,
                    className: "add-success",
                });
            } else {
                setIsLoading(false);
                if (response.status === 420) {
                    toast.warn(response.body.error, {
                        position: "bottom-right",
                        autoClose: 10000,
                        closeOnClick: true,
                        className: "product-background",
                        pauseOnHover: true,
                    });
                } else {
                    navigate("/error", {
                        state: {
                            status: response.status,
                            message: response.body ? response.body.error : "Error",
                        },
                    });
                }
            }
        }
    };

    const fetchData = async () => {
        const response = await fetchWorkshopServiceTable(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/workshop-service/table`,
            token,
            {
                draw: 1,
                start: itemOffset,
                length: itemsPerPage,
                recordsTotal: 0,
                columns: [],
                order: [{ column: sortColumn, dir: sortDir }],
                search: { value: "", regex: true },
                filter: { workshopId: null },
            }
        );
        if (response.success) {
            setIsLoading(false);
            setClear(false);
            const reviewData = response.data.data.map((item) => ({
                id: item.id,
                name: item.name,
                serviceType: item.serviceType ? item.serviceType : "—",
                calculationType: item.calculationType ? item.calculationType.value : "—",
                price: item.price,
                currency: item.currency ? item.currency.name : "—",
                actions: (
                    <>
                        <EditButton
                            onclick={() => navigate(`/workshop-service/update/${item.id}`)}
                        />
                        <DeleteButton onclick={() => onDeleteItem(item.id)} />
                    </>
                ),
            }));
            setData({
                data: reviewData,
                recordsTotal: response.data.recordsFiltered,
                pageCount: Math.ceil(response.data.recordsFiltered / parseInt(itemsPerPage)),
            });
        } else {
            navigate("/error", {
                state: {
                    status: response.status,
                    message: response.message ? response.message : null,
                },
            });
        }
    };

    useEffect(() => {
        if (clear) {
            fetchData();
        }
    }, [clear]);

    useEffect(() => {
        fetchData();
    }, [itemOffset, itemsPerPage]);

    const handlePageClick = (event) => {
        setForcePage(event.selected);
        const newOffset =
            (event.selected * parseInt(itemsPerPage)) % data.recordsTotal;
        setItemOffset(newOffset);
    };

    const sortFunction = (value) => {
        setSortColumn(value.field ? value.field : "id");
        setSortDir(value.sort === true ? "ASC" : "DESC");
        setClear(true);
    };

    return (
        <>
            {isLoading ? (
                <Loading />
            ) : (
                <div className="px-4 py-4 sm:px-6 lg:px-8">
                    <div className="mb-2 sm:flex sm:items-center">
                        <div className="sm:flex-auto">
                            <h1 className="text-xl font-semibold text-gray-900">
                                {t("workshop_service")}
                            </h1>
                        </div>
                        <AddButton title={t("add_workshop_service")} url={"add"} />
                    </div>
                    <div>
                        <Select
                            setItemsPerPage={setItemsPerPage}
                            itemsPerPage={itemsPerPage}
                        />
                        <TempDataTable
                            data={data.data}
                            headerTable={headerTable}
                            bodyContent={bodyTable}
                            sortFunction={sortFunction}
                        />
                        {data?.pageCount !== 0 && (
                            <Pagination
                                handlePageClick={handlePageClick}
                                pageCount={data.pageCount}
                                forcePage={forcePage}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

export default withTranslation()(WorkshopServices);
